import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { FormBody, Row, Label, Select } from './AddWorkflowEvidenceForm.styles'

export type Option = {
  name: string
  pid: string
  pname: string
  tid: string
  tname: string
  tpid: string
}

export type WorkflowFormState = {
  project: string
  taskType: string
  taskName: string
  title: string
  description: string
  siteUrl: string
  label: string
}

export type AddWorkflowEvidenceFormProps = {
  modalId: ModalFormId
  isShown: boolean
  onSubmitFunc: (data: WorkflowFormState) => Promise<void> | void
  configs?: Option[]
  siteUrl: string
  label: string
  controlId: string
  description: string
}

export const AddWorkflowEvidenceForm: React.FC<
  AddWorkflowEvidenceFormProps
> = ({
  modalId,
  isShown,
  onSubmitFunc,
  configs = [],
  siteUrl = '',
  label = '',
  controlId = '',
  description = '',
}) => {
  const makeInitial = useCallback(
    (
      siteUrl?: string,
      label?: string,
      controlId?: string,
    ): WorkflowFormState => ({
      project: '',
      taskType: '',
      taskName: '',
      title: `[${controlId}] Request for evidence`,
      description: description || '',
      siteUrl: siteUrl || '',
      label: label || '',
    }),
    [description],
  )

  const [state, setState] = useState<WorkflowFormState>(() =>
    makeInitial(siteUrl, label, controlId),
  )

  const handleConfigChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value)
    const sel = Number.isNaN(idx) ? undefined : configs[idx]
    if (!sel) return
    setState((s) => ({
      ...s,
      project: sel.pid,
      taskType: sel.tid,
      taskName: sel.tname,
    }))
  }

  useEffect(() => {
    if (!isShown) setState(makeInitial(siteUrl, label, controlId))
  }, [isShown, siteUrl, label, controlId, description, makeInitial])

  // Initialize default from first config if none selected yet
  useEffect(() => {
    if (configs.length > 0 && (!state.project || !state.taskType)) {
      setState((s) => ({
        ...s,
        project: s.project || configs[0].pid,
        taskType: s.taskType || configs[0].tid,
        taskName: s.taskName || configs[0].tname,
      }))
    }
  }, [configs, state.project, state.taskType])

  const selectedIndex = useMemo(
    () =>
      configs.findIndex(
        (c) => c.pid === state.project && c.tid === state.taskType,
      ),
    [configs, state.project, state.taskType],
  )

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!state.project || !state.title?.trim()) return
    await onSubmitFunc(state)
  }

  return (
    <FormBody id={modalId} onSubmit={onSubmit}>
      <Row>
        <Label htmlFor="config">Configuration</Label>
        <Select
          id="config"
          value={selectedIndex >= 0 ? String(selectedIndex) : ''}
          onChange={handleConfigChange}
        >
          {configs.map((c, i) => (
            <option key={`${c.pid}-${c.tid}`} value={i}>
              {`${c.name} (${c.pname} - ${c.tname})`}
            </option>
          ))}
        </Select>
      </Row>
    </FormBody>
  )
}
